import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, Input, ViewChild,
  ElementRef, Output, EventEmitter, Inject, OnDestroy
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { isNullOrUndefined } from '@app/util/util';
import { BillingCity, BillingResponse } from '@billing/entities/billing.payload';
import { PERSON_FORM_INPUTS, PERSON_ADDRESS_FORM_INPUTS } from '@booking/constants/forms.constants';
import { CountriesVm } from '@booking/entities/countries';
import { BillingFormService } from '@billing/services/billing-form.service';
import { UserAddresses } from '@app/modules/core/interfaces/address';
import { AssetBookingConfig } from '@asset-booking-config';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';

@Component({
  selector: 'app-address-form',
  templateUrl: './address-form.container.html',
  styleUrls: ['./address-form.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AddressFormContainer implements OnDestroy {

  @Input() form: UntypedFormGroup;
  @Input() countries: CountriesVm[] = [];
  @Input() cities: BillingCity[] = [];
  @Input() userAddresses: UserAddresses;
  @Input() defaultBilling: BillingResponse;
  @Input() isActiveForm: boolean;
  @Input() isSubmitted: boolean;
  @Input() disabledCountryOptions: boolean;
  @Input() isMobile: boolean;

  @Input() set selectedCityCode(sc: string) {
    this._selectedCityCode = sc;
    this.selectedCityCodeSubject$.next(this.selectedCityCode);
  }

  @Output() selectedCountry: EventEmitter<string> = new EventEmitter();
  @Output() selectedCity: EventEmitter<string> = new EventEmitter();

  @ViewChild('cityElementRef') public cityRef: ElementRef;
  @ViewChild('countryElementRef') public countryRef: ElementRef;
  @ViewChild('streetElementRef') public streetRef: ElementRef;
  @ViewChild('stateElementRed') public stateRef: ElementRef;
  @ViewChild('cepElementRef') public cepRef: ElementRef;
  @ViewChild('neighborhoodElementRef') public neighborhoodRef: ElementRef;
  @ViewChild('complementElementRef') public complementRef: ElementRef;

  public countryFormControl: string = PERSON_ADDRESS_FORM_INPUTS.COUNTRY_CODE;
  public streetFormControl: string = PERSON_ADDRESS_FORM_INPUTS.STREET;
  public cepFormControl: string = PERSON_ADDRESS_FORM_INPUTS.POSTAL_CODE;
  public neighborhoodFormControl: string = PERSON_ADDRESS_FORM_INPUTS.NEIGHBORHOOD;
  public complementFormControl: string = PERSON_ADDRESS_FORM_INPUTS.COMPLEMENT;

  private _selectedCityCode: string = null;

  private selectedCityCodeSubject$: BehaviorSubject<string | null> = new BehaviorSubject(null);

  constructor(
    private readonly billingFormService: BillingFormService,
    @Inject(IS_BROWSER) public isBrowser: boolean,
  ) { }

  ngOnDestroy(): void {
    if (this.selectedCityCodeSubject$) this.selectedCityCodeSubject$.unsubscribe();
  }

  get defaultCity$(): Observable<string> {
    return this.selectedCityCodeSubject$.pipe(
      switchMap(selectedCityCode => this.billingFormService.defaultCityCode$(
        this.defaultBilling, this.userAddresses, this.cities, this.defaultCountryCode
      ).pipe(
        map(defaultCity => {
          return !isNullOrUndefined(selectedCityCode) ? selectedCityCode :
            defaultCity && !isNullOrUndefined(this.cities?.find(c => c.code === defaultCity)) ? defaultCity : '';
        })
      ))
    );
  }

  get selectedCityCode(): string {
    return this._selectedCityCode;
  }

  get isActiveFullAddressForm(): boolean {
    return !!AssetBookingConfig.BILLING_MODULE.ENABLED_FULL_ADDRESS_FORM;
  }

  get addressForm(): UntypedFormGroup {
    return this.form ? this.form?.get(PERSON_FORM_INPUTS.ADDRESS) as UntypedFormGroup : null;
  }

  get defaultCountryCode(): string {
    return this.addressForm && this.addressForm.get(PERSON_ADDRESS_FORM_INPUTS.COUNTRY_CODE) ?
      this.addressForm.get(PERSON_ADDRESS_FORM_INPUTS.COUNTRY_CODE).value : '';
  }

  get defaultStateCode(): string {
    return this.addressForm && this.addressForm.get(PERSON_ADDRESS_FORM_INPUTS.STATE_CODE) ?
      this.addressForm.get(PERSON_ADDRESS_FORM_INPUTS.STATE_CODE).value : '';
  }

  public changeUpperCaseName(event: Event, inputType: string, form: UntypedFormGroup): void {
    this.billingFormService.changeUpperCasePersonNames(form, event.target['value'], inputType);
  }

  public scrollToAddressInputRef(elementRef: ElementRef): void {
    this.billingFormService.scrollIntoView(elementRef);
  }

}
